import { type RefObject, useCallback, useEffect, useRef } from "react";
import {
  type AvatarCallbacks,
  HedraAvatarManager,
  type LiveKitTokenResponse,
} from "../hedra-avatar-manager";
import type { WidgetState } from "../types";

interface UseHedraAvatarManagerOptions {
  videoRef: RefObject<HTMLVideoElement | null>;
  onStateChange?: (state: WidgetState) => void;
  onStreamReady?: () => void;
  onDisconnected?: () => void;
  onAgentSpeaking?: () => void;
  onAgentListening?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for managing Hedra avatar lifecycle via LiveKit
 *
 * In the new architecture:
 * - User audio is published to the room automatically
 * - The Python agent handles VAD, STT, LLM, and TTS
 * - Agent state changes are communicated via data channel or participant attributes
 */
export function useHedraAvatarManager(options: UseHedraAvatarManagerOptions) {
  const {
    videoRef,
    onStateChange,
    onStreamReady,
    onDisconnected,
    onAgentSpeaking,
    onAgentListening,
    onError,
  } = options;

  const avatarManagerRef = useRef<HedraAvatarManager | null>(null);

  const initialize = useCallback(
    async (tokenResponse: LiveKitTokenResponse) => {
      if (!videoRef.current) {
        throw new Error("Video element not available");
      }

      if (avatarManagerRef.current) {
        console.warn("Avatar already initialized");
        return;
      }

      const callbacks: AvatarCallbacks = {
        onStreamReady,
        onDisconnected,
        onAgentSpeaking,
        onAgentListening,
        onError,
        onStateChange,
      };

      avatarManagerRef.current = new HedraAvatarManager({
        tokenResponse,
        videoElement: videoRef.current,
        callbacks,
      });

      await avatarManagerRef.current.initialize(tokenResponse);
    },
    [
      videoRef,
      onStreamReady,
      onDisconnected,
      onAgentSpeaking,
      onAgentListening,
      onError,
      onStateChange,
    ]
  );

  const destroy = useCallback(async () => {
    if (!avatarManagerRef.current) return;
    await avatarManagerRef.current.destroy();
    avatarManagerRef.current = null;
  }, []);

  const isInitialized = useCallback(
    () => avatarManagerRef.current?.isInitialized() ?? false,
    []
  );

  const setMicrophoneEnabled = useCallback(async (enabled: boolean) => {
    if (!avatarManagerRef.current) return;
    await avatarManagerRef.current.setMicrophoneEnabled(enabled);
  }, []);

  useEffect(
    () => () => {
      void destroy();
    },
    [destroy]
  );

  return {
    initialize,
    destroy,
    isInitialized,
    setMicrophoneEnabled,
  };
}
